import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import api from 'apis/api';

import { useAuthStore } from 'store/index';
import { useWorkspaceStore } from 'store/workspace.store';

import type { Policy } from 'types/auth';

interface UseAccessControlOptions {
  /** Control when the query is enabled (default: true when workspace is set) */
  enabled?: boolean;
}

export const useAccessControl = (options: UseAccessControlOptions = {}) => {
  const { currentWorkspaceId } = useWorkspaceStore();
  const { user } = useAuthStore();
  const { setUserAccessibleModules, setUserPolicies } = useAuthStore();

  // Default enabled to true if workspace is set, but allow override
  const isEnabled = options.enabled ?? !!currentWorkspaceId;
  const queryClient = useQueryClient();

  const query = useQuery<Policy[], Error>({
    queryKey: ['access-control', currentWorkspaceId, user?.email],
    queryFn: async ({ queryKey }) => {
      // Get workspace ID from queryKey to avoid stale closure issues
      const workspaceId = queryKey[1] as string;
      const apiUrl = `/access-control/user_access/${workspaceId}`;

      // Skip loader on refetches (when cached data exists)
      const skipLoader = !!queryClient.getQueryData(queryKey);
      const { data } = await api.get<Policy[]>(apiUrl, {
        skipLoader,
      });

      // Store the raw policies for permission checks
      setUserPolicies(data);

      // Grab all unique modules from the user's policies
      const userAccessibleModules = data
        .flatMap((policy) => policy.modules)
        .filter((module) => module !== null);

      // Deduplicate modules by name
      const uniqueUserAccessibleModules = userAccessibleModules.filter(
        (module, index, self) =>
          self.findIndex((m) => m.name === module.name) === index
      );

      setUserAccessibleModules(uniqueUserAccessibleModules);

      return data;
    },
    enabled: isEnabled && !!currentWorkspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Fail closed: if the query fails after retries, set empty modules/policies to deny access
  // This ensures PermissionGuard doesn't leave routes unprotected indefinitely
  useEffect(() => {
    if (query.isError) {
      setUserAccessibleModules([]);
      setUserPolicies([]);
    }
  }, [query.isError, setUserAccessibleModules, setUserPolicies]);

  return query;
};
